import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap rounded-md font-medium transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none cursor-pointer',
  {
    variants: {
      variant: {
        // Primary action button - box shadow on hover only (size handled by size variant)
        primary:
          'bg-[var(--action-btn-primary-bg)] text-[var(--action-btn-primary-text)] hover:bg-[var(--action-btn-primary-hover-bg)] hover:shadow-[var(--action-btn-primary-hover-shadow)] active:bg-[var(--action-btn-primary-active-bg)] active:shadow-[var(--action-btn-primary-active-shadow)] disabled:bg-[var(--action-btn-primary-disabled-bg)] disabled:text-[var(--action-btn-primary-disabled-text)] disabled:shadow-none font-semibold gap-2 focus-visible:ring-[var(--action-btn-primary-focus-ring)]',
        // Navigation button - used in sidebar
        nav: 'btn-nav',
        // Default (alias to primary for compatibility)
        default:
          'bg-[var(--action-btn-primary-bg)] text-[var(--action-btn-primary-text)] hover:bg-[var(--action-btn-primary-hover-bg)] hover:shadow-[var(--action-btn-primary-hover-shadow)] active:bg-[var(--action-btn-primary-active-bg)] active:shadow-[var(--action-btn-primary-active-shadow)] disabled:bg-[var(--action-btn-primary-disabled-bg)] disabled:text-[var(--action-btn-primary-disabled-text)] disabled:shadow-none font-semibold gap-2 focus-visible:ring-[var(--action-btn-primary-focus-ring)]',
        // Destructive
        destructive:
          'bg-[var(--color-error)] text-white hover:brightness-90 active:brightness-75 disabled:opacity-50 font-semibold gap-2 focus-visible:ring-[var(--color-error)] shadow-sm',
        // Ghost / Outline
        ghost: 'bg-transparent text-[var(--action-btn-primary-bg)] hover:bg-[var(--action-btn-primary-hover-bg)]/10',
        outline:
          'bg-transparent border border-[var(--action-btn-primary-bg)] text-[var(--action-btn-primary-bg)] hover:bg-[var(--action-btn-primary-bg)] hover:text-white',
        // Secondary button - neutral, less prominent action
        secondary:
          'bg-[var(--action-btn-secondary-bg)] text-[var(--action-btn-secondary-text)] border border-[var(--action-btn-secondary-border)] hover:bg-[var(--action-btn-secondary-hover-bg)] hover:border-[var(--action-btn-secondary-hover-border)] active:bg-[var(--action-btn-secondary-active-bg)] disabled:bg-[var(--action-btn-secondary-disabled-bg)] disabled:text-[var(--action-btn-secondary-disabled-text)] disabled:border-[var(--action-btn-secondary-disabled-border)] font-semibold gap-2 focus-visible:ring-[var(--action-btn-secondary-focus-ring)]',
        // Alert/Warning button - for attention-grabbing actions
        alert:
          'bg-[var(--action-btn-alert-bg)] text-[var(--action-btn-alert-text)] hover:bg-[var(--action-btn-alert-hover-bg)] hover:shadow-[var(--action-btn-alert-hover-shadow)] active:bg-[var(--action-btn-alert-active-bg)] active:shadow-[var(--action-btn-alert-active-shadow)] disabled:bg-[var(--action-btn-alert-disabled-bg)] disabled:text-[var(--action-btn-alert-disabled-text)] disabled:shadow-none font-semibold gap-2 focus-visible:ring-[var(--action-btn-alert-focus-ring)]',
      },
      size: {
        default: 'h-11 px-5 text-sm',
        sm: 'h-9 px-3 text-sm rounded-md',
        xs: 'h-6 px-2 text-[10px] rounded-md font-normal',
        lg: 'h-12 px-8 text-base rounded-lg',
        icon: 'h-11 w-11',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'default',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  /** Whether the button is in an active/selected state (used for navigation) */
  isActive?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, isActive, ...props }, ref) => {
    return (
      <button
        className={cn(
          buttonVariants({ variant, size, className }),
          isActive && variant === 'nav' && 'btn-nav--selected'
        )}
        ref={ref}
        data-active={isActive}
        {...props}
      />
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
